/**
 * @module MapControls
 */
import DropdownControl from './controls/DropdownControl.js';
import ToggleRadiosControl from './controls/ToggleRadiosControl.js';
import LanguageControl from './controls/LanguageControl.js';
import OpacitySliderControl from './controls/OpacitySliderControl.js';
import SaveGisControl from './controls/SaveGisControl.js';
import SavePngControl from './controls/SavePngControl.js';
import ToggleLabelsControl from './controls/ToggleLabelsControl.js';
import ZoomToControl from './controls/ZoomToControl.js';
import { getLayerByName } from './utils/getLayerByName.js';

class MapControls {
  constructor() {
    // Keep track of the controls we've created
    this.controls = [];
  }

  /**
   * Set the language for the product manager
   * @param {string} newLanguage - The new language code (e.g., 'en', 'es')
   */
  setLanguage(newLanguage) {
    this.language = newLanguage;
  }

  // This method is now called by MapManager
  loadControlsFromConfigs(configs, mapManager) {
    // 1. Merge control configurations from all products
    const allControlConfigs = configs.flatMap(config => config.controls || []);
    const uniqueControlConfigs = this._getUniqueConfigs(allControlConfigs, 'type');

    // 2. Separate dropdown controls from others
    const dropdownConfigs = uniqueControlConfigs.filter(c => c.container === 'dropdown');
    const rootControls = uniqueControlConfigs.filter(c => c.container !== 'dropdown');

    // 3. Create and populate the dropdown control
    // We build these first because one control, specifically, affects the
    // contents of the dropdown (different downloads), so the dropdown elements
    // had to be built first. Don't know if that will affect future controls.
    if (dropdownConfigs.length > 0) {
      const dropdown = new DropdownControl();
      this.controls.push(dropdown); // Store the instance
      const dropdownContainer = dropdown.getContainerElement();
      dropdownConfigs.forEach(config => {
        config.target = dropdownContainer;
        const controlInstance = this.createControl(config, mapManager);
        if (controlInstance) {
          this.controls.push(controlInstance); // Store the instance
          dropdown.addControl(controlInstance);
        }
      });
      mapManager.map.addControl(dropdown);
    }

    // 4. Create and add controls that live on the map root
    rootControls.forEach(config => {
      config.target = mapManager.map.getTargetElement();
      const control = this.createControl(config, mapManager);
      if (control) {
        this.controls.push(control); // Store the instance
        mapManager.map.addControl(control);
      }
    });

  }

  // Factory method to create a single control instance
  createControl(config, mapManager) {
    switch (config.type) {
      case 'language':
        return new LanguageControl({ 
          target: config.target,
          mapManager: mapManager,
          languages: config.languages,
          currentLanguage: mapManager.language,
          label: config.label
        });
      case "opacitySlider":
        // Only create the control if there are watches and warnings
        if (config.filename) {
          return new OpacitySliderControl({ 
            target: config.target,
            map: mapManager.map,
            productId: config.productId,
            layerId: config.layerId,
            styleModule: config.styleModule
          });
        }
        else {
          return null;
        }
      case "saveGis":
        // Only create the control if config.filename !== ""
        if (config.filename) {
          return new SaveGisControl({ 
            target: config.target,
            id: config.id,
            label: config.label,
            filename: config.filename
          });
        }
        else {
          return null;
        }
      case 'savePng':
        return new SavePngControl({ 
          target: config.target,
          id: config.id,
          label: config.label,
          filename: config.filename
        });
      case "toggleLabels":
        return new ToggleLabelsControl({ 
          target: config.target,
          map: mapManager.map
        });
      case "toggleRadios":
        const startLayer = getLayerByName(mapManager.map, config.startLayerId);
        const nextLayer = getLayerByName(mapManager.map, config.nextLayerId);
        const comboLayer = getLayerByName(mapManager.map, config.comboLayerId);
        return new ToggleRadiosControl({
          map: mapManager.map,
          target: document.getElementById(config.position),
          startLayer: startLayer,
          startLayerName: config.startLayerName,
          startLayerDate: config.startLayerDate,
          startLayerId: config.startLayerId,
          nextLayer: nextLayer,
          nextLayerName: config.nextLayerName,
          nextLayerDate: config.nextLayerDate,
          nextLayerId: config.nextLayerId,
          comboLayer: comboLayer,
          comboLayerName: config.comboLayerName,
          comboLayerId: config.comboLayerId,
        });
      case "zoomTo":
        return new ZoomToControl({ 
          target: config.target,
          map: mapManager.map
        });
      // ... other cases
      default:
        return null;
    }
  }

  _getUniqueConfigs(configs, key) {
    return [...new Map(configs.map(item => [item[key], item])).values()];
  }

  /**
   * Iterates through all managed controls and tells them to update their language.
   */
  updateControlLanguages() {
    for (const control of this.controls) {
      // Check if the control has an updateLanguage method before calling it
      if (typeof control.updateLanguage === 'function') {
        control.updateLanguage();
      }
    }
  }

  addControls(controlsConfig) {
    for (const controlConfig of controlsConfig) {
      this.addControl(controlConfig);
    }
  }

  removeControl(controlId) {
    const control = this.controls[controlId];
    if (control) {
      this.map.removeControl(control);
      delete this.controls[controlId];
    }
  }

  removeControls(controlsConfig) {
    for (const controlConfig of controlsConfig) {
      this.removeControl(controlConfig.id);
    }
  }

}

export default MapControls;